import { useEffect, useRef } from 'react';
import { useStillFrameReadyRegistration } from '@/components/preview/still-frame-ready-context';

const imageStyle: React.CSSProperties = {
  maxWidth: '100%',
  maxHeight: '100%',
  width: 'auto',
  height: 'auto',
  objectFit: 'contain',
  display: 'block',
};

export function StillFrameImage({ url, alt }: { url: string; alt: string }) {
  const settleReady = useStillFrameReadyRegistration(true);
  const imgRef = useRef<HTMLImageElement>(null);
  const readySentRef = useRef(false);

  useEffect(() => {
    readySentRef.current = false;
  }, [url]);

  useEffect(() => {
    const img = imgRef.current;
    if (!img || !settleReady) {
      return;
    }

    const signalReady = () => {
      if (readySentRef.current) {
        return;
      }
      readySentRef.current = true;
      settleReady();
    };

    // Cached images often fire load before this effect — `complete` covers that race.
    if (img.complete) {
      signalReady();
      return;
    }

    img.addEventListener('load', signalReady);
    img.addEventListener('error', signalReady);
    return () => {
      img.removeEventListener('load', signalReady);
      img.removeEventListener('error', signalReady);
    };
  }, [url, settleReady]);

  return <img ref={imgRef} key={url} src={url} alt={alt} style={imageStyle} />;
}
